/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfileInfo } from '../../../../state/thunks/users';
// utils
import { validation } from '../../../../utils/validation';
// material
import { Page, Content, CardWrapper, Group, Copy, Title, Form, Grid, GridSet, GridItem, TextField, Button, InContentMessage } from '@insticator/insticator-ui'; // iui-material
import PageHeader from "../../../../material/structure/pageheader/pageheader";
import PageContent from "../../../../material/structure/pagecontent/pagecontent";
// content
// styles
import injectSheet from "react-jss";
import styles from './account_styles';

const formName = 'personalInformationForm'
/* ========== ~~~~~~~~~~ MY ACCOUNT PAGE ~~~~~~~~~~ ========== */
const AccountPage = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const { mocked, userFirstName, userLastName, userEmail, userPhoneNumber, userUUID, updatingProfileInfo } = useSelector(state => state.session);

  const [isEditable, setEditable] = useState(false)

  const [profile, setProfile] = useState({
    fname: userFirstName,
    lname: userLastName,
    email: userEmail,
    phone: userPhoneNumber
  })

  const handleProfileFormChange = e => {
    const {value, name} = e.target
    setProfile({
      ...profile,
      [name]: value
    })
  }
  const handleSave = useCallback(async () => {
    const data = {
      firstName: profile.fname,
      lastName: profile.lname,
      email: profile.email,
      phoneNumber: profile.phone
    }
    const response = await dispatch(updateProfileInfo({data, userUUID}))
    if(!response.error) setEditable(false)
  }, [profile]);

  const handleDiscard = () => {
    setProfile({
      fname: userFirstName,
      lname: userLastName,
      email: userEmail,
      phone: userPhoneNumber
    })
    setEditable(false)
  }
 
  const isValid = name => validation[name].test.test(profile[name])

  // Fix for not showing the validation on page load due to the admin panel allowing numeric characters
  // TODO: Optionally remove this once we match the first/last name validation on admin panel & PP side.
  const isNameValid = name => isValid(name) || !isEditable 

  const isFormValid = useMemo(() => isValid('fname') && isValid('lname') && (isValid('phone') || !profile.phone), [profile, isEditable])

  return (
    <Page name={'account'} klass={`${props.classes.account}`}>
      <Content>
        <InContentMessage />
        <PageHeader title={'Settings'} subtitle={'My Account'} />
        <PageContent>
          <CardWrapper>
            <Form name={formName} processing={updatingProfileInfo}>
              <Content>
                <Copy name={`page-copy`}>
                  <Title><h2>Personal Information</h2></Title>
                </Copy>
                <Group name={`page-details`}>
                  <Grid type={`fields`}>
                      <GridSet direction={`horizontal`}>
                          <GridItem>
                            <TextField 
                              type={`text`} 
                              formName={formName} 
                              name={`fname`} 
                              placeholder={`First Name`} 
                              label={`First Name`} 
                              value={profile.fname}  
                              valid={isNameValid('fname')} 
                              handleChange={handleProfileFormChange} 
                              disabled={mocked || !isEditable} 
                              required 
                            />
                          </GridItem>
                          <GridItem>
                            <TextField 
                              type={`text`} 
                              formName={formName} 
                              name={`lname`} 
                              placeholder={`Last Name`} 
                              label={`Last Name`} 
                              value={profile.lname}  
                              valid={isNameValid('lname')}
                              handleChange={handleProfileFormChange} 
                              disabled={mocked || !isEditable} 
                              required />
                          </GridItem>
                      </GridSet>
                      <GridSet direction={`horizontal`}>
                          <GridItem>
                            <TextField 
                              type={`text`} 
                              formName={formName} 
                              name={`email`} 
                              placeholder={`Email`} 
                              label={`Email`}
                              value={profile.email} 
                              valid={!!profile.email}
                              disabled={true} 
                              required 
                            />
                          </GridItem>
                      </GridSet>
                      <GridSet>
                          <GridItem>
                            <TextField 
                              type={`text`} 
                              formName={formName} 
                              name={`phone`} 
                              placeholder={`Phone Number`} 
                              label={`Phone Number`}
                              value={profile.phone}
                              valid={isValid(`phone`) || !profile.phone}
                              handleChange={handleProfileFormChange} 
                              disabled={mocked || !isEditable} 
                            />
                            { !isValid('phone') && profile.phone && <div className={'errormessage'}>You must enter a valid phone number</div>}
                          </GridItem>
                      </GridSet>
                      <Group name={'profileaction'}>
                      { isEditable ?
                        <>
                          <Button name={'save'} variant={'primary'} text={'Save'} processing={updatingProfileInfo} operation={'save'} handleClick={handleSave} disabled={!isFormValid} />
                          <Button name={'discard'} variant={'system'} text={'Discard Changes'} handleClick={handleDiscard} />
                        </>
                        :
                        <Button name={'edit'} variant={'primary'} text={'Edit Information'} disabled={mocked} handleClick={() => setEditable(true)} /> 
                      }
                      </Group>
                  </Grid>
                </Group>
              </Content>
            </Form>
          </CardWrapper>
        </PageContent>
      </Content>
    </Page>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default AccountPage;